import { GraphQLClient } from "graphql-request";
import { getSdk } from "./graphql";
import { API_URL } from "./apiClient";

const STORAGE_KEYS = {
  ACCESS_TOKEN: "accessToken_172349817234908172349087",
  REFRESH_TOKEN: "refreshToken_172349817234908172349087",
  ACCESS_EXPIRES: "accessExpiresAt_172349817234908172349087",
  REFRESH_EXPIRES: "refreshExpiresAt_172349817234908172349087",
} as const;

export interface TokenData {
  accessToken: string;
  refreshToken: string;
  accessExpiresIn: number;
  refreshExpiresIn: number;
}

class TokenManager {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private accessExpiresAt: number | null = null;
  private refreshExpiresAt: number | null = null;
  private refreshPromise: Promise<TokenData> | null = null;

  constructor() {
    this.loadFromStorage();
    // Sincronización entre PESTAÑAS: si otra tab refresca o limpia los tokens,
    // adoptar su estado (localStorage es la fuente de verdad compartida).
    if (typeof window !== "undefined") {
      window.addEventListener("storage", (e) => {
        if (
          e.key &&
          (Object.values(STORAGE_KEYS) as string[]).includes(e.key)
        ) {
          this.loadFromStorage();
        }
      });
    }
  }

  private loadFromStorage(): void {
    try {
      this.accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      this.refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      const accessExpires = localStorage.getItem(STORAGE_KEYS.ACCESS_EXPIRES);
      const refreshExpires = localStorage.getItem(STORAGE_KEYS.REFRESH_EXPIRES);

      this.accessExpiresAt = accessExpires ? parseInt(accessExpires, 10) : null;
      this.refreshExpiresAt = refreshExpires
        ? parseInt(refreshExpires, 10)
        : null;
    } catch (error) {
      console.error("Error loading tokens from storage:", error);
      this.clearTokens();
    }
  }

  saveTokens(tokens: TokenData): void {
    const now = Date.now();
    this.accessToken = tokens.accessToken;
    this.refreshToken = tokens.refreshToken;
    // Convert seconds to milliseconds and add to current time
    this.accessExpiresAt = now + tokens.accessExpiresIn * 1000;
    this.refreshExpiresAt = now + tokens.refreshExpiresIn * 1000;

    try {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
      localStorage.setItem(
        STORAGE_KEYS.ACCESS_EXPIRES,
        this.accessExpiresAt.toString()
      );
      localStorage.setItem(
        STORAGE_KEYS.REFRESH_EXPIRES,
        this.refreshExpiresAt.toString()
      );
    } catch (error) {
      console.error("Error saving tokens to storage:", error);
    }
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.accessExpiresAt = null;
    this.refreshExpiresAt = null;
    this.refreshPromise = null;

    try {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.ACCESS_EXPIRES);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_EXPIRES);
    } catch (error) {
      console.error("Error clearing tokens from storage:", error);
    }
  }

  isAccessTokenExpired(): boolean {
    if (!this.accessExpiresAt) return true;
    // Add 30 second buffer before expiration
    return Date.now() >= this.accessExpiresAt - 30000;
  }

  isRefreshTokenExpired(): boolean {
    if (!this.refreshExpiresAt) return true;
    // Add 30 second buffer before expiration
    return Date.now() >= this.refreshExpiresAt - 30000;
  }

  async getValidAccessToken(): Promise<string | null> {
    // Fuente de verdad compartida entre pestañas: otra tab pudo refrescar ya.
    this.loadFromStorage();

    // If refresh token is expired, user needs to login again
    if (this.isRefreshTokenExpired()) {
      this.clearTokens();
      return null;
    }

    // If access token is still valid, return it
    if (!this.isAccessTokenExpired() && this.accessToken) {
      return this.accessToken;
    }

    // If already refreshing, wait for that promise
    if (this.refreshPromise) {
      try {
        await this.refreshPromise;
        return this.accessToken;
      } catch {
        return null;
      }
    }

    // Need to refresh the access token
    if (this.refreshToken) {
      try {
        this.refreshPromise = this.refreshWithCrossTabLock();
        await this.refreshPromise;
        return this.accessToken;
      } catch (error) {
        console.error("Error refreshing token:", error);
        // refreshTokens ya limpió si el rechazo fue definitivo; ante errores
        // transitorios (red/429) conservamos los tokens para reintentar.
        return null;
      } finally {
        this.refreshPromise = null;
      }
    }

    return null;
  }

  /**
   * Refresh con lock ENTRE PESTAÑAS (Web Locks API): una sola tab rota el
   * token; las demás esperan el lock, releen storage y adoptan el par nuevo
   * sin rotar (evita la detección de reuso del server).
   */
  private async refreshWithCrossTabLock(): Promise<TokenData> {
    const doRefresh = async (): Promise<TokenData> => {
      // Re-chequear DENTRO del lock: si otra tab ya refrescó, adoptar.
      this.loadFromStorage();
      if (
        !this.isAccessTokenExpired() &&
        this.accessToken &&
        this.refreshToken
      ) {
        return {
          accessToken: this.accessToken,
          refreshToken: this.refreshToken,
          accessExpiresIn: 0,
          refreshExpiresIn: 0,
        };
      }
      return this.refreshTokens();
    };
    const locks =
      typeof navigator !== "undefined" ? (navigator as any).locks : undefined;
    if (locks?.request) {
      return locks.request("token-refresh-172349817234908172349087", doRefresh);
    }
    return doRefresh();
  }

  private async refreshTokens(): Promise<TokenData> {
    if (!this.refreshToken) {
      throw new Error("No refresh token available");
    }

    const url = `${API_URL}/graphql`;
    const client = new GraphQLClient(url, { headers: {} });
    const api = getSdk(client);

    try {
      const data = await api.RefreshToken({
        refreshToken: this.refreshToken,
      });

      const tokens: TokenData = {
        accessToken: data.refreshToken.accessToken,
        refreshToken: data.refreshToken.refreshToken,
        accessExpiresIn: data.refreshToken.accessExpiresIn,
        refreshExpiresIn: data.refreshToken.refreshExpiresIn,
      };

      this.saveTokens(tokens);
      return tokens;
    } catch (error: any) {
      // Limpiar SOLO ante rechazo DEFINITIVO del server (token inválido,
      // expirado, revocado o reuso). Errores transitorios (red caída, 429,
      // 5xx) NO desloguean: el próximo intento reintenta con el mismo token.
      const msg = String(
        error?.response?.errors?.[0]?.message ?? error?.message ?? ""
      );
      if (/invalid|expired|revoked|reuse|not found/i.test(msg)) {
        this.clearTokens();
      }
      throw error;
    }
  }

  getAuthState() {
    return {
      accessToken: this.accessToken,
      refreshToken: this.refreshToken,
      accessExpiresAt: this.accessExpiresAt,
      refreshExpiresAt: this.refreshExpiresAt,
    };
  }

  hasValidTokens(): boolean {
    return !!(
      this.accessToken &&
      this.refreshToken &&
      !this.isRefreshTokenExpired()
    );
  }
}

// Singleton instance
export const tokenManager = new TokenManager();
